import { isValidTaxId } from '../utils/tax-id.js';
import { z } from 'zod';
import { hhmm, isoDate, isoDateTime, money, uuid } from './common.js';
export const interval = z.object({ start: hhmm, end: hhmm }).refine((v) => v.start < v.end, { message: 'INVALID_INTERVAL' });
export const dayHours = z.object({
    open: z.boolean(),
    intervals: z.array(interval).max(4),
    lunch: interval.nullable(),
});
export const weeklyHoursSchema = z.object({ '0': dayHours, '1': dayHours, '2': dayHours, '3': dayHours, '4': dayHours, '5': dayHours, '6': dayHours });
/** Horário individual do membro: null em um dia = segue o negócio. */
export const memberWeeklyHoursSchema = z.partialRecord(z.enum(['0', '1', '2', '3', '4', '5', '6']), z.object({ intervals: z.array(interval).max(4) }).nullable());
export const businessProfileSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    segment: z.string().trim().max(80).nullable().optional(),
    address: z.string().trim().max(300).nullable().optional(),
    phone: z.string().trim().max(32).nullable().optional(),
    timezone: z.string().min(3).max(64).optional(),
    aiLocale: z.enum(['pt-BR', 'en-US']).optional(),
    modality: z.enum(['LOCAL', 'HOME', 'BOTH']).optional(),
    taxId: z.string().trim().max(20).nullable().optional().refine((v) => !v || isValidTaxId(v), { message: 'INVALID_TAX_ID' }),
});
export const businessSettingsSchema = z.object({
    scheduling: z.object({
        minBookingNoticeMin: z.number().int().min(0).max(60 * 24 * 30),
        minCancelNoticeMin: z.number().int().min(0).max(60 * 24 * 30),
        minRescheduleNoticeMin: z.number().int().min(0).max(60 * 24 * 30),
        maxOpenAppointmentsPerClient: z.number().int().min(1).max(50),
        aiHoldMinutes: z.number().int().min(1).max(60),
        allowMultiProfessionalSequence: z.boolean(),
    }).partial(),
    reminders: z.object({
        enabled: z.boolean(),
        requireConfirmation: z.boolean(),
        hoursBefore: z.array(z.number().min(0.5).max(168)).max(4),
        unansweredAction: z.enum(['KEEP', 'RELEASE']),
        unansweredDeadlineHours: z.number().min(0.5).max(72),
    }).partial(),
    noShow: z.object({
        threshold: z.number().int().min(1).max(20),
        action: z.enum(['REQUIRE_CONFIRMATION', 'SUSPEND_AI_BOOKING', 'NOTIFY_ADMIN']),
        suspendDays: z.number().int().min(1).max(365).nullable(),
    }).partial(),
    routing: z.object({ rule: z.enum(['ASK_PREFERENCE', 'FIRST_AVAILABLE', 'ROUND_ROBIN', 'LAST_ATTENDED']) }).partial(),
    substitution: z.object({ enabled: z.boolean() }).partial(),
    waitlist: z.object({ holdMinutes: z.number().int().min(5).max(240), cascadeLevels: z.number().int().min(1).max(3) }).partial(),
    conversation: z.object({
        humanInactivityMin: z.number().int().min(5).max(720),
        resumeWarningMin: z.number().int().min(1).max(120),
        warnBeforeResume: z.boolean(),
        automationsContinueDuringHuman: z.boolean(),
    }).partial(),
    standardUserClientVisibility: z.enum(['ALL', 'OWN', 'NONE']),
    antiAbuse: z.object({ maxMessagesPerContactPerDay: z.number().int().min(5).max(500), maxTurnsWithoutGoal: z.number().int().min(3).max(100) }).partial(),
    homeService: z.object({ mode: z.enum(['REGIONS', 'RADIUS']), radiusKm: z.number().min(0.5).max(200).nullable(), requireFullAddress: z.boolean() }).partial(),
    summaries: z.object({ dailySummaryAt: z.string().regex(/^\d{2}:\d{2}$/), nextReminderMin: z.number().int().min(0).max(120), weeklyReportDay: z.number().int().min(0).max(6), weeklyReportAt: z.string().regex(/^\d{2}:\d{2}$/) }).partial(),
    approvals: z.object({ rescheduleRequiresApproval: z.boolean(), cancelRequiresApproval: z.boolean() }).partial(),
    reactivation: z.object({ maxPerDay: z.number().int().min(1).max(1000), windowStart: z.string().regex(/^\d{2}:\d{2}$/), windowEnd: z.string().regex(/^\d{2}:\d{2}$/), minDaysBetweenEngagement: z.number().int().min(1).max(365) }).partial(),
}).partial();
export const featureFlagsSchema = z.object({
    waitlist: z.boolean(), substitution: z.boolean(), homeService: z.boolean(), reactivation: z.boolean(), whatsappCommands: z.boolean(), resources: z.boolean(),
    /** Encaixe pela equipe (fora de horário, bloqueio, sobreposição, duração encurtada). */
    fitIn: z.boolean(),
}).partial();
export const memberUpdateSchema = z.object({
    role: z.enum(['ADMIN', 'STANDARD']).optional(),
    canBeSubstitute: z.boolean().optional(),
    dailySummaryEnabled: z.boolean().optional(),
    whatsappNoticesEnabled: z.boolean().optional(),
    /** Push do app: avisos da agenda no celular. */
    pushEnabled: z.boolean().optional(),
    /** Admin: recebe push de todas as agendas (false = só da própria). */
    pushAllAgendas: z.boolean().optional(),
    colorIndex: z.number().int().min(0).max(4).optional(),
    workingHours: memberWeeklyHoursSchema.nullable().optional(),
    serviceIds: z.array(uuid).optional(),
});
export const memberBlockSchema = z.object({
    businessMemberId: uuid,
    startsAt: isoDateTime,
    endsAt: isoDateTime,
    kind: z.enum(['TIME_OFF', 'VACATION', 'BLOCK']).default('BLOCK'),
    reason: z.string().trim().max(200).nullable().optional(),
}).refine((v) => v.endsAt > v.startsAt, { message: 'INVALID_INTERVAL', path: ['endsAt'] });
export const holidaySchema = z.object({ date: isoDate, name: z.string().trim().min(1).max(120), closesAt: hhmm.nullable().optional() });
export const serviceSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    durationMin: z.number().int().min(5).max(24 * 60),
    price: money.nullable().optional(),
    priceVisibility: z.enum(['SHOW', 'HIDE', 'ON_REQUEST']).default('SHOW'),
    allowsHome: z.boolean().default(false),
    bufferBeforeMin: z.number().int().min(0).max(240).default(0),
    bufferAfterMin: z.number().int().min(0).max(240).default(0),
    recommendedReturnDays: z.number().int().min(1).max(730).nullable().optional(),
    /** Serviço pode entrar numa série (recorrência). Desligado para serviço único, ex.: avaliação inicial. */
    allowsRecurrence: z.boolean().default(true),
    requiredResourceTypeId: uuid.nullable().optional(),
    requiredResourceId: uuid.nullable().optional(),
    isCombo: z.boolean().default(false),
    comboItems: z.array(z.object({ serviceId: uuid, order: z.number().int().min(0) })).default([]),
    providerMemberIds: z.array(uuid).default([]),
    active: z.boolean().default(true),
    sortOrder: z.number().int().default(0),
});
export const resourceTypeSchema = z.object({ name: z.string().trim().min(1).max(80) });
export const resourceSchema = z.object({ typeId: uuid, name: z.string().trim().min(1).max(80), status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE') });
export const resourceBlockSchema = z.object({ startsAt: isoDateTime, endsAt: isoDateTime, reason: z.string().trim().max(200).nullable().optional() }).refine((v) => v.endsAt > v.startsAt, { message: 'INVALID_INTERVAL', path: ['endsAt'] });
export const regionSchema = z.object({ name: z.string().trim().min(1).max(80), travelTimeMin: z.number().int().min(0).max(600), fee: money.nullable().optional(), active: z.boolean().default(true) });
export const aiSettingsSchema = z.object({
    assistantName: z.string().trim().min(1).max(40),
    tone: z.enum(['WARM', 'DIRECT', 'FORMAL']),
    systemPrompt: z.string().max(4000),
    mustKnow: z.array(z.string().trim().min(1).max(300)).max(50),
    mustNotDo: z.array(z.string().trim().min(1).max(300)).max(50),
    faq: z.array(z.object({ question: z.string().trim().min(1).max(300), answer: z.string().trim().min(1).max(1000) })).max(100),
    outOfHoursMessage: z.string().max(600),
    contingencyMessage: z.string().max(600),
    handoffBehavior: z.enum(['HANDOFF', 'SAY_UNKNOWN']),
    appointmentNotesRules: z.string().max(1500),
}).partial();
//# sourceMappingURL=catalog.js.map